import React, { useState, useRef, useEffect } from 'react';
import { FiPhone, FiChevronDown } from 'react-icons/fi';
import { COUNTRY_CODES, searchCountries, DEFAULT_COUNTRY, CountryCode, getCountryCodes } from '@/utils/countryCodes';

interface PhoneInputProps {
    value: string;
    onChange: (value: string, countryInfo: CountryCode, fullNumber: string) => void;
    placeholder?: string;
    className?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    autoComplete?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
    value,
    onChange,
    placeholder = "Enter phone number",
    className = "",
    error,
    required = false,
    disabled = false,
    autoComplete = "tel"
}) => {
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(DEFAULT_COUNTRY);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [availableCountries, setAvailableCountries] = useState<CountryCode[]>(COUNTRY_CODES);
    const [filteredCountries, setFilteredCountries] = useState(COUNTRY_CODES);
    const [isLoadingCountries, setIsLoadingCountries] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Load countries from API on component mount
    useEffect(() => {
        const loadCountries = async () => {
            setIsLoadingCountries(true);
            try {
                const countries = await getCountryCodes();
                setAvailableCountries(countries);
                setFilteredCountries(countries);
            } catch (error) {
                console.warn('Failed to load countries, using fallback:', error);
            } finally {
                setIsLoadingCountries(false);
            }
        };

        loadCountries();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
                setSearchQuery('');
                setFilteredCountries(availableCountries);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [availableCountries]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isDropdownOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isDropdownOpen]);

    // Handle search
    const handleSearch = (query: string) => {
        setSearchQuery(query);
        setFilteredCountries(searchCountries(query, availableCountries));
    };

    // Handle country selection
    const handleCountrySelect = (country: CountryCode) => {
        setSelectedCountry(country);
        setIsDropdownOpen(false);
        setSearchQuery('');
        setFilteredCountries(availableCountries);

        // Call onChange with current phone number and complete country info
        const fullNumber = country.dialCode + value.replace(/^\+?\d+\s*/, '');
        onChange(value, country, fullNumber);
    };

    // Handle phone number input
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onChange(newValue, selectedCountry, selectedCountry.dialCode + newValue);
    };

    // Toggle dropdown
    const toggleDropdown = () => {
        if (!disabled) {
            setIsDropdownOpen(!isDropdownOpen);
        }
    };

    return (
        <div className="relative">
            <div className={`flex border border-gray-300 dark:border-gray-600 rounded-lg focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 ${className} ${error ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500' : ''}`}>
                {/* Country Code Dropdown */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        type="button"
                        onClick={toggleDropdown}
                        disabled={disabled}
                        className={`flex items-center px-3 py-3 border-r border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 rounded-l-lg transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <span className="text-lg mr-1">{selectedCountry.flag}</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1">
                            {selectedCountry.dialCode}
                        </span>
                        <FiChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown */}
                    {isDropdownOpen && (
                        <div className="absolute top-full left-0 z-50 w-72 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-hidden">
                            {/* Search Input */}
                            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    placeholder="Search countries..."
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            {/* Countries List */}
                            <div className="overflow-y-auto max-h-40">
                                {isLoadingCountries ? (
                                    <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mr-2"></div>
                                        Loading countries...
                                    </div>
                                ) : filteredCountries.length > 0 ? (
                                    filteredCountries.map((country) => (
                                        <button
                                            key={country.code}
                                            type="button"
                                            onClick={() => handleCountrySelect(country)}
                                            className={`w-full flex items-center px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 transition-colors ${selectedCountry.code === country.code ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
                                        >
                                            <span className="text-lg mr-3">{country.flag}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {country.name}
                                                </div>
                                            </div>
                                            <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                                                {country.dialCode}
                                            </span>
                                        </button>
                                    ))
                                ) : (
                                    <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                                        No countries found
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Phone Number Input */}
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiPhone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="tel"
                        value={value}
                        onChange={handlePhoneChange}
                        placeholder={placeholder}
                        required={required}
                        disabled={disabled}
                        autoComplete={autoComplete}
                        className="appearance-none block w-full pl-10 pr-3 py-3 border-0 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-r-lg focus:outline-none bg-white dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-500">{error}</p>
            )}
        </div>
    );
};

export default PhoneInput;